import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiPlaneLine, RiBuilding2Line, RiMenuAddLine, RiStarFill, RiAlertLine, RiMedalLine, RiAwardLine, RiArrowRightLine, RiCheckLine, RiAddLine, RiSubtractLine, RiSpeedUpLine, RiCalendarEventLine, RiTeamLine, RiCheckboxCircleFill } from '@remixicon/react';
import { Root as Button } from '@components/ui/Button';
import { Skeleton } from '@components/ui/Skeleton';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/Drawer';
import { TrustScore } from '@/features/trust-score';
import { apiClient } from '@/lib/api';
import { OperatorDetailsDrawer } from '@/features/operators';
import { getAircraftModelName, getOperatorWithLocation, getAircraftPhotos, getAircraftType, getAircraftProperty } from '@/features/tails/utils';
import { useAppStore } from '@/store/appStore';
import ImageCarousel from '@components/ui/ImageCarousel';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS } from '@/lib/permissions';
import { resolveAssetUrl } from '@/utils/urls';

const TailDetailsDrawer = ({ isOpen, onClose, tail, onShortlist }) => {
  const navigate = useNavigate();
  const { addToShortlist, clearSelectedTripGroup } = useAppStore();
  const [activeTab, setActiveTab] = useState('aircraft');
  const [expandedItems, setExpandedItems] = useState({});
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [operatorDrawerOpen, setOperatorDrawerOpen] = useState(false);
  const tabsRef = useRef(null);

  // Shortlist starts the quote-building workflow → requires full CRUD on
  // Booking & Quoting (admin + broker only).
  const { can } = usePermissions();
  const canShortlistTail =
    can(MODULES.BOOKING, ACTIONS.CREATE) &&
    can(MODULES.BOOKING, ACTIONS.UPDATE) &&
    can(MODULES.BOOKING, ACTIONS.DELETE);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    // Scroll tabs into view so they stay at their sticky position
    requestAnimationFrame(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, []);

  // Fetch operator data using React Query
  const { data: operator } = useQuery({
    queryKey: ['operator', tail?.operator_id, 'with-includes'],
    queryFn: () => apiClient.getOperatorById(tail.operator_id, {
      include: 'ntsb_incidents,ucc_verifications,trust_scores,financial_records'
    }),
    enabled: isOpen && !!tail?.operator_id,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Fetch aircraft photos using React Query - only when no direct image available
  const { data: aircraftPhotos, isFetching: isFetchingPhotos } = useQuery({
    queryKey: ['aircraftPhotos', tail?.aircraft_id],
    queryFn: () => apiClient.getAircraftPhotos(tail.aircraft_id),
    enabled: isOpen && !!tail?.aircraft_id && !tail.jetnet_exists,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Fetch aircraft details by make_model
  const { data: aircraftDetails, isFetching: isFetchingDetails } = useQuery({
    queryKey: ['aircraftDetails', tail?.aircraft_id],
    queryFn: () => apiClient.getAircraftById(tail?.aircraft_id, { include: 'make_model, features' }),
    enabled: isOpen && !!tail?.aircraft_id && !tail.jetnet_exists,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time drawer opens
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: 1000, // Wait 1 second between retries
  });

  // Derive model name from aircraftDetails or tail
  const modelName = getAircraftModelName(aircraftDetails || tail);

  const aircraftType = getAircraftType(aircraftDetails || tail);

  // Get all aircraft photos as an array for the carousel
  const allAircraftPhotos = aircraftPhotos?.photos?.length > 0
    ? aircraftPhotos?.photos?.map(photo => resolveAssetUrl(photo))
    : getAircraftPhotos(tail, true);

  const toggleAccordion = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleShortlist = async () => {
    if (onShortlist && tail) {
      setIsShortlisting(true);
      try {
        await onShortlist(tail.aircraft_id);
      } finally {
        setIsShortlisting(false);
        onClose();
      }
      return;
    }
    if (tail) {
      addToShortlist(tail);
    }
    clearSelectedTripGroup();
    onClose();
    navigate('/quotes/generate');
  };

  if (!tail) return null;

  const tabs = [
    { id: 'aircraft', label: 'Aircraft Details' },
    { id: 'operator', label: 'Operator Profile' },
    { id: 'aog', label: 'AOG Reports', badge: 0 }
  ];

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="right" className="w-full !max-w-2xl overflow-y-auto !p-0 [&>button]:z-20 !z-[250]" overlayClassName="!z-[250]">
        {/* Header */}
        <div className="sticky top-0 bg-white/70 backdrop-blur-sm border-0 px-6 pt-4 z-10">
          <DrawerHeader className="flex-row items-start justify-between mb-4">
            {isFetchingDetails ? (
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <Skeleton className="h-8 w-28" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
            ) : (
              <div className="flex-1">
                <DrawerTitle className="flex items-center gap-2 mb-2 !text-2xl font-medium text-gray-900">
                  {getAircraftProperty(aircraftDetails || tail, ['tail_number'])}
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-white whitespace-nowrap border font-normal border-gray-200 text-gray-700`}>
                   <RiCheckboxCircleFill className={`w-4 h-4 ${(getAircraftProperty(aircraftDetails || tail, ['current_status', 'status']) === 'AVAILABLE') ? 'text-green-400' : 'text-red-700'}`} />
                    {getAircraftProperty(aircraftDetails || tail, ['current_status', 'status'])?.charAt(0).toUpperCase() + getAircraftProperty(aircraftDetails || tail, ['current_status', 'status'])?.slice(1).toLowerCase()}
                  </span>
                </DrawerTitle>
              </div>
            )}
          </DrawerHeader>
        </div>

        <div className="px-6">
          <div className="flex items-center gap-1 text-gray-600 mb-1">
            <RiPlaneLine className="w-4 h-4" />
            <span className="text-sm">{modelName || 'Unknown Model'}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <RiBuilding2Line className="w-4 h-4" />
            <span className="text-sm">{getOperatorWithLocation(operator)}</span>
          </div>
        </div>

        {/* Aircraft Image Carousel */}
        <div className="px-6 pt-4">
          {isFetchingPhotos ? (
            <div className="w-full h-[272px] rounded-2xl bg-gray-200 animate-pulse" />
          ) : (
            <ImageCarousel
              images={allAircraftPhotos}
              alt={tail.tail_number || 'Aircraft'}
              className="h-[272px]"
            />
          )}
        </div>

        {/* Trust Score Section */}
        <div className="px-6 py-4">
          {isFetchingDetails ? (
            <div className="bg-gray-100 rounded-full border border-gray-200 p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-16 h-16 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-24" />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-24 rounded-full" />
                    <Skeleton className="h-8 w-32 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 rounded-full border border-green-200 py-4 px-2 md:py-4 md:px-4">
              <div className="flex items-center gap-2 md:gap-4 ml-5">
                {/* Trust Score Circle */}
                <TrustScore score={operator?.rating_tiers} numericScore={operator?.trust_score || 0} label="Trust" size="md" strokeWidth={10} />

                {/* Badges */}
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-600 mb-2">TRUSTSCORE</div>
                  <div>
                      <TrustScore score={operator?.rating_tiers} numericScore={null} label="Trust" size="md" strokeWidth={10} align="left" showBadge />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {operator?.argus_rating && operator?.argus_rating!='No' &&
                    <span className={`flex items-center gap-1 px-1.5 py-1 bg-black/10 text-gray-700 text-xs rounded-full font-medium uppercase`}>
                      <RiMedalLine className="w-4 h-4" /> {operator?.argus_rating}
                    </span> }

                    {operator?.wyvern_rating && operator?.wyvern_rating!='No' &&
                    <span className={`flex items-center gap-1 px-1.5 py-1 bg-black/10 text-gray-700 text-xs rounded-full font-medium`}>
                      <RiAwardLine className="w-4 h-4" /> WYVERN WINGMAN
                    </span>
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Status Cards */}
        <div className="px-6 pb-4">
          {isFetchingDetails ? (
            <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-2xl">
              <div className="p-4 flex items-start gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
              <div className="p-4 flex items-start gap-3">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-3 w-36" />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 border border-gray-200 rounded-2xl">
              <div className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 text-xl"><RiStarFill className="w-6 h-6 text-green-500" /></span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Excellent</div>
                  <div className="text-xs text-gray-600">Fleet and Operator Profile</div>
                </div>
              </div>
              <div className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-600 text-xl"><RiAlertLine className="w-6 h-6 text-yellow-500"/></span>
                </div>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Caution</div>
                  <div className="text-xs text-gray-600">Aircraft Condition and History</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div ref={tabsRef} className="sticky top-[56px] bg-white/70 backdrop-blur-sm px-6 border-b border-gray-200 mt-3 z-10">
          {isFetchingDetails ? (
            <div className="flex gap-6 justify-center pb-3">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-5 w-24" />
            </div>
          ) : (
            <div className="flex gap-6 justify-center">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`pb-3 text-xs md:text-sm font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? 'text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  {tab.badge !== undefined && (
                    <span className="ml-2 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                      {tab.badge}
                    </span>
                  )}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tab Content */}
        <div key={activeTab} className="px-6 py-6 min-h-[calc(100vh-120px)] animate-fadeIn">
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } } .animate-fadeIn { animation: fadeIn 0.2s ease-in; }`}</style>
          {activeTab === 'aircraft' && (
          <>
            {isFetchingDetails ? (
              <>
                {/* About This Aircraft Skeleton */}
                <div className="border border-gray-200 rounded-2xl p-4">
                  <Skeleton className="h-4 w-40 mb-4" />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-5 w-28" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-5 w-20" />
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <div className="space-y-2">
                        <Skeleton className="h-3 w-32" />
                        <Skeleton className="h-5 w-16" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Amenities Skeleton */}
                <div className="border border-gray-200 rounded-2xl p-4 mt-3">
                  <Skeleton className="h-4 w-24 mb-4" />
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <Skeleton className="h-5 w-32 mt-2" />
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <Skeleton className="h-5 w-40 mt-2" />
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="w-10 h-10 rounded-full" />
                      <Skeleton className="h-5 w-36 mt-2" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="border border-gray-200 rounded-2xl p-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-4">ABOUT THIS AIRCRAFT</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RiPlaneLine className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Aircraft Type</div>
                        <div className="text-xs md:text-sm font-medium text-gray-900">{aircraftType}</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RiTeamLine className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Seating Capacity</div>
                        <div className="text-xs md:text-sm font-medium text-gray-900">{getAircraftProperty(aircraftDetails || tail, ['passenger_capacity', 'max_passengers'], ' Passengers')}</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RiSpeedUpLine className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Range</div>
                        <div className="text-xs md:text-sm font-medium text-gray-900">{(aircraftDetails || tail)?.aircraft_make_model?.min_range} - {(aircraftDetails || tail)?.aircraft_make_model?.max_range} nm</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RiCalendarEventLine className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Year of Manufacture</div>
                        <div className="text-xs md:text-sm font-medium text-gray-900">{getAircraftProperty(aircraftDetails || tail, ['mfr_year', 'year'])}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-2xl p-4 mt-3">
                  <h3 className="text-sm font-semibold text-gray-600 mb-4">AMENITIES</h3>
                  {(aircraftDetails || tail)?.aircraft_features?.custom_features?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-0">
                      {(aircraftDetails || tail).aircraft_features.custom_features.map((feature, index) => (
                        <div key={index} className="flex">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <RiCheckLine className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-gray-900 mt-2">{feature}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No amenities included.</p>
                  )}
                </div>
              </>
            )}

            
          </>
          )}

          {activeTab === 'operator' && (
            <div className="space-y-4">
              {/* Positives Section */}
              <div className="border border-gray-200 rounded-2xl p-4">

                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 text-lg">{operator?.name || 'Unknown Operator'}</h3>
                    <p className="text-sm text-gray-500">{(operator?.faa_city || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}{operator?.faa_state ? ", "+operator.faa_state.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : ''}</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center flex-shrink-0">
                    <RiPlaneLine className="w-6 h-6 text-white" />
                  </div>
                </div>

                <div className="flex">
                  {/* Visit Operator Profile Link */}
                  <button
                    onClick={() => navigate(`/operators/${tail?.operator_id}`)}
                    className="w-full mt-4 flex items-center bg-gray-100 rounded-full px-3 justify-between py-3 transition-colors"
                  >
                    <span className="text-sm text-gray-700">Visit Operator Profile</span>
                    <RiArrowRightLine className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <h4 className="text-xs font-medium text-gray-500 mb-3 mt-5">Positives</h4>
                <div className="space-y-1">
                  {/* Safety & Compliance Rating */}
                  <div className={`border border-gray-100 rounded-lg ${expandedItems['safety'] ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => toggleAccordion('safety')}
                      className="w-full flex items-center justify-between py-3 px-2"
                    >
                      <div className="flex items-center gap-3">
                        <RiCheckLine className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-900">Safety & Compliance Rating</span>
                      </div>
                      {expandedItems['safety'] ? (
                        <RiSubtractLine className="w-5 h-5 text-gray-400" />
                      ) : (
                        <RiAddLine className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedItems['safety'] && (
                      <div className="pb-3 pl-10 pr-2 text-sm">
                        {operator?.argus_rating === "No" && operator?.wyvern_rating === "No" ? (
                          <><span className="text-gray-400">No Safety & Compliance Rating</span></>
                        ) : (
                          <>
                            {operator?.argus_rating && operator?.argus_rating !== "No" && (
                              <p className="text-gray-700">ARGUS {operator?.argus_rating}</p>
                            )}
                            {operator?.wyvern_rating && operator?.wyvern_rating !== "No" && (
                              <p className="text-gray-700">Wyvern Wingman</p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Financial Standing */}
                  <div className={`border border-gray-100 rounded-lg ${expandedItems['financial'] ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => toggleAccordion('financial')}
                      className="w-full flex items-center justify-between py-3 px-2"
                    >
                      <div className="flex items-center gap-3">
                        <RiCheckLine className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-900">Financial Standing</span>
                      </div>
                      {expandedItems['financial'] ? (
                        <RiSubtractLine className="w-5 h-5 text-gray-400" />
                      ) : (
                        <RiAddLine className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedItems['financial'] && (
                      
                      operator?.ucc_verifications?.length < 1 ? (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-400">
                        No significant risks identified from UCC filings.
                        </div>
                      ) : (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-700">
                          {operator?.ucc_verifications?.map((data, index) => (
                            <div key={index} className="mb-2">
                              {data.source && (
                                <p className="font-medium">{data.source}</p>
                              )}
                              {data.verification_date && (
                                <p className="text-xs mt-1">Verified: {data.verification_date}</p>
                              )}
                              {data.status && (
                                <p className="text-xs mt-1">Status: {data.status}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )

                    )}
                  </div>

                  {/* Legal Standing */}
                  <div className={`border border-gray-100 rounded-lg ${expandedItems['legal'] ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => toggleAccordion('legal')}
                      className="w-full flex items-center justify-between py-3 px-2"
                    >
                      <div className="flex items-center gap-3">
                        <RiCheckLine className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-900">Legal Standing</span>
                      </div>
                      {expandedItems['legal'] ? (
                        <RiSubtractLine className="w-5 h-5 text-gray-400" />
                      ) : (
                        <RiAddLine className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedItems['legal'] && (
                      operator?.ntsb_incidents?.length < 1 ? (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-400">
                          Clear of FAA enforcement actions and major NTSB reports.
                        </div>
                      ) : (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-700">
                          <p className="font-medium mb-2">
                            {operator?.ntsb_incidents?.length} NTSB report{operator?.ntsb_incidents?.length > 1 ? 's' : ''} found
                          </p>
                          {operator?.ntsb_incidents?.map((incident, index) => (
                            <div key={index} className="mb-2 border-l-2 border-gray-300 pl-2">
                              <p className="font-medium">{incident.event_date || 'Unknown date'}</p>
                              <p>{incident.description || incident.event_type || 'No description'}</p>
                              {incident.investigation_status && (
                                <p className="text-xs mt-1">Status: {incident.investigation_status}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>

                  {/* Incident History */}
                  <div className={`border border-gray-100 rounded-lg ${expandedItems['incident'] ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => toggleAccordion('incident')}
                      className="w-full flex items-center justify-between py-3 px-2"
                    >
                      <div className="flex items-center gap-3">
                        <RiCheckLine className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-900">Incident History</span>
                      </div>
                      {expandedItems['incident'] ? (
                        <RiSubtractLine className="w-5 h-5 text-gray-400" />
                      ) : (
                        <RiAddLine className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedItems['incident'] && (
                      operator?.ntsb_incidents?.length < 1 ? (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-400">
                          No operator-level incidents or accidents in the last 5 years.
                        </div>
                      ) : (
                        <div className="pb-3 pl-10 pr-2 text-sm text-gray-700">
                          <p className="font-medium mb-2">
                            {operator?.ntsb_incidents?.length} incident{operator?.ntsb_incidents?.length > 1 ? 's' : ''} on record
                          </p>
                          {operator?.ntsb_incidents?.map((incident, index) => (
                            <div key={index} className="mb-2 border-l-2 border-gray-300 pl-2">
                              <p className="font-medium">{incident.event_date || 'Unknown date'}</p>
                              <p>{incident.description || incident.event_type || 'No description'}</p>
                              {incident.investigation_status && (
                                <p className="text-xs mt-1">Status: {incident.investigation_status}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Advisories & Risks Section */}
                <h4 className="text-xs font-medium text-gray-500 mb-3 mt-5">Advisories & Risks</h4>
                <div className="space-y-1">
                  <div className={`border border-gray-100 rounded-lg ${expandedItems['operational'] ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => toggleAccordion('operational')}
                      className="w-full flex items-center justify-between py-3 px-2"
                    >
                      <div className="flex items-center gap-3">
                        <RiAlertLine className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-900">Operational History</span>
                      </div>
                      {expandedItems['operational'] ? (
                        <RiSubtractLine className="w-5 h-5 text-gray-400" />
                      ) : (
                        <RiAddLine className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedItems['operational'] && (
                      <div className="pb-3 pl-10 pr-2 text-sm">
                        {operator?.business_started_date ? (<><span className="text-gray-700">
                          {`${new Date().getFullYear() - new Date(tail.operator.business_started_date).getFullYear()} Years (Est. ${new Date(tail.operator.business_started_date).getFullYear()})`}</span></>
                        ) : '-'}

                        <p className='mt-5 w-1/2 block italic text-gray-400'>Note: While not inherently a cause for concern, operators with over a decade of history are usually preferred. This is a minor flag for general awareness.</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

          {activeTab === 'aog' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 mb-4">AOG REPORTS</h3>
              <div className="text-center py-8 text-gray-500">
                No AOG reports available for this aircraft
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions — only roles with full CRUD on Booking & Quoting.
            Fixed-positioned with z-[1001] so it floats above the mobile
            BottomNavBar (z-1000). bottom-[72px] on mobile clears the nav;
            bottom-0 on md+ where the nav is hidden. Right-aligned and width-
            capped to overlay only the drawer area, not the full viewport. */}
        {canShortlistTail && (
          <div className="fixed bottom-[72px] md:bottom-0 left-0 right-0 md:left-auto md:w-full md:max-w-2xl bg-white border-t border-gray-200 px-6 py-4 z-[1001]">
            <div className="flex gap-3">
              <Button
                variant="primary"
                mode="filled"
                size="medium"
                className="flex-1 rounded-2xl !bg-primary-700 !font-normal !text-sm"
                onClick={handleShortlist}
                disabled={isShortlisting}
              >
                {isShortlisting
                  ? 'Shortlisting...'
                  : <><RiMenuAddLine className="h-4 w-4"/> Shortlist {getAircraftProperty(aircraftDetails || tail, ['tail_number'])}</>
                }
              </Button>
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>

      {/* Operator Details Drawer */}
      <OperatorDetailsDrawer
        isOpen={operatorDrawerOpen}
        onClose={() => setOperatorDrawerOpen(false)}
        operatorId={tail?.operator_id}
      />
    </>
  );
};

export default TailDetailsDrawer;
